<template>
  <teleport to="body">
    <transition name="more-sheet">
      <div v-if="moreOpen" class="mobile-more-overlay fixed inset-0 z-40 md:hidden">
        <button
          class="absolute inset-0 h-full w-full bg-gray-950/35 backdrop-blur-[2px]"
          type="button"
          tabindex="-1"
          aria-hidden="true"
          @click="closeMore"
        ></button>

        <section
          id="mobile-more-menu"
          ref="morePanelEl"
          class="absolute inset-x-3 rounded-3xl bg-white p-3 shadow-2xl ring-1 ring-black/[0.06] dark:bg-gray-900 dark:ring-white/10"
          :style="morePanelStyle"
          role="dialog"
          aria-modal="true"
          :aria-label="$t('nav_more')"
          @keydown.tab="trapFocus"
        >
          <div class="flex min-h-11 items-center justify-between px-2">
            <h2 class="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
              {{ $t('nav_more') }}
            </h2>
            <button
              class="flex h-11 w-11 items-center justify-center rounded-full text-gray-500 transition-[background-color,scale] duration-150 hover:bg-gray-100 active:scale-[0.96] dark:text-gray-300 dark:hover:bg-gray-800"
              type="button"
              :aria-label="$t('close')"
              @click="closeMore"
            >
              <XIcon class="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <RouterLink
            v-if="auth.user"
            data-more-primary
            class="mt-1 flex min-h-14 items-center gap-3 rounded-2xl bg-gray-50 px-3 py-2.5 transition-[background-color,scale] duration-150 hover:bg-gray-100 active:scale-[0.96] dark:bg-gray-800/80 dark:hover:bg-gray-800"
            to="/profile"
            @click="closeMore"
          >
            <img
              :alt="$t('auth.avatar')"
              :src="auth.user.avatar || '/avatar_placeholder.png'"
              class="h-10 w-10 shrink-0 rounded-full object-cover outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10"
              @error="handleAvatarError"
            />
            <span class="min-w-0 flex-1">
              <span class="block truncate text-sm font-semibold text-gray-900 dark:text-white">
                {{ auth.user.name }}
              </span>
              <span class="block text-xs text-gray-500 dark:text-gray-400">
                {{ $t('nav_profile') }}
              </span>
            </span>
            <ChevronRightIcon class="h-5 w-5 shrink-0 text-gray-400 rtl:rotate-180" aria-hidden="true" />
          </RouterLink>

          <button
            v-else
            data-more-primary
            class="mt-1 flex min-h-14 w-full items-center gap-3 rounded-2xl bg-indigo-50 px-3 py-2.5 text-start transition-[background-color,scale] duration-150 hover:bg-indigo-100 active:scale-[0.96] dark:bg-indigo-950/40 dark:hover:bg-indigo-950/60"
            type="button"
            @click="openLogin"
          >
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white">
              <LogInIcon class="h-5 w-5" aria-hidden="true" />
            </span>
            <span class="text-sm font-semibold text-indigo-800 dark:text-indigo-200">
              {{ $t('auth.login') }}
            </span>
          </button>

          <div class="mt-2 grid gap-1">
            <RouterLink
              v-for="link in menuLinks"
              :key="link.name"
              :to="link.to"
              class="flex min-h-12 items-center gap-3 rounded-2xl px-3 py-2 text-sm font-medium transition-[background-color,scale] duration-150 active:scale-[0.96]"
              :class="isActive(link)
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300'
                : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'"
              @click="closeMore"
            >
              <component :is="link.icon" class="h-5 w-5" aria-hidden="true" />
              <span class="flex-1">{{ $t(link.label) }}</span>
              <ChevronRightIcon class="h-5 w-5 text-gray-400 rtl:rotate-180" aria-hidden="true" />
            </RouterLink>
          </div>
        </section>
      </div>
    </transition>
  </teleport>

  <nav
    v-if="route.path !== '/upgrade'"
    class="bottom-nav fixed bottom-0 left-0 right-0 z-50 flex w-full items-stretch bg-white/95 shadow-[0_-1px_0_rgba(17,24,39,0.08),0_-8px_24px_rgba(17,24,39,0.04)] backdrop-blur-xl md:hidden dark:bg-gray-950/95 dark:shadow-[0_-1px_0_rgba(255,255,255,0.08)]"
    style="height: calc(var(--nav-height) + env(safe-area-inset-bottom, 0px)); padding: 0 env(safe-area-inset-right, 0px) env(safe-area-inset-bottom, 0px) env(safe-area-inset-left, 0px);"
    :aria-label="$t('nav_primary')"
  >
    <template v-for="tab in tabs" :key="tab.name">
      <button
        v-if="tab.action === 'more'"
        id="mobile-more-button"
        class="group flex min-h-14 flex-1 basis-0 flex-col items-center justify-center gap-0.5 px-1 py-1 text-center transition-[color,scale] duration-150 active:scale-[0.96]"
        :class="isActive(tab) || moreOpen ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500 dark:text-gray-400'"
        type="button"
        :aria-expanded="moreOpen"
        aria-controls="mobile-more-menu"
        @click="toggleMore"
      >
        <span
          class="flex h-7 min-w-10 items-center justify-center rounded-full px-2 transition-colors duration-150"
          :class="isActive(tab) || moreOpen ? 'bg-emerald-100 dark:bg-emerald-500/15' : 'group-hover:bg-gray-100 dark:group-hover:bg-gray-800'"
        >
          <component :is="tab.icon" class="h-5 w-5" aria-hidden="true" />
        </span>
        <span class="max-w-full truncate text-[11px] font-medium leading-4">{{ $t(tab.label) }}</span>
      </button>

      <RouterLink
        v-else
        :to="tab.to"
        class="group flex min-h-14 flex-1 basis-0 flex-col items-center justify-center gap-0.5 px-1 py-1 text-center transition-[color,scale] duration-150 active:scale-[0.96]"
        :class="isActive(tab) && !moreOpen ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500 dark:text-gray-400'"
        :aria-current="isActive(tab) && !moreOpen ? 'page' : undefined"
        @click="closeMore"
      >
        <span
          class="flex h-7 min-w-10 items-center justify-center rounded-full px-2 transition-colors duration-150"
          :class="isActive(tab) && !moreOpen ? 'bg-emerald-100 dark:bg-emerald-500/15' : 'group-hover:bg-gray-100 dark:group-hover:bg-gray-800'"
        >
          <component :is="tab.icon" class="h-5 w-5" aria-hidden="true" />
        </span>
        <span class="max-w-full truncate text-[11px] font-medium leading-4">{{ $t(tab.label) }}</span>
      </RouterLink>
    </template>
  </nav>
</template>

<script setup>
import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ChevronRightIcon, LogInIcon, XIcon } from 'lucide-vue-next'
import { usePrimaryNav } from '@/composables/usePrimaryNav.js'
import { useAuthStore } from '@/stores/auth.js'
import { handleAvatarError } from '@/utils/imageHelpers'

const route = useRoute()
const auth = useAuthStore()
const openAuthModal = inject('openAuthModal', null)
const { tabs, moreLinks, isActive } = usePrimaryNav()

const moreOpen = ref(false)
const morePanelEl = ref(null)

const menuLinks = computed(() => moreLinks.value.filter((link) => link.name !== 'Profile'))
const inertSelector = '#app, .bottom-nav'
const morePanelStyle = {
  bottom: 'calc(var(--nav-height) + env(safe-area-inset-bottom, 0px) + 0.75rem)',
}

watch(() => route.fullPath, closeMore)

onMounted(() => document.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown)
  setBackgroundInert(false)
})

function setBackgroundInert (inert) {
  document.querySelectorAll(inertSelector).forEach((el) => {
    if (inert) el.setAttribute('inert', '')
    else el.removeAttribute('inert')
  })
}

function toggleMore () {
  if (moreOpen.value) closeMore()
  else openMore()
}

function openMore () {
  moreOpen.value = true
  nextTick(() => {
    setBackgroundInert(true)
    morePanelEl.value?.querySelector('[data-more-primary]')?.focus()
  })
}

function closeMore () {
  if (!moreOpen.value) return
  moreOpen.value = false
  setBackgroundInert(false)
  nextTick(() => document.getElementById('mobile-more-button')?.focus())
}

function trapFocus (event) {
  const focusable = [...morePanelEl.value.querySelectorAll('a[href], button:not([disabled])')]
  if (!focusable.length) return
  const first = focusable[0]
  const last = focusable.at(-1)
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function openLogin () {
  closeMore()
  openAuthModal?.('', 'login')
}

function onKeydown (event) {
  if (event.key !== 'Escape' || !moreOpen.value) return
  closeMore()
}
</script>

<style scoped>
:global(html.native-platform) .bottom-nav {
  display: flex !important;
}

:global(html.native-platform) .mobile-more-overlay {
  display: block !important;
}

.more-sheet-enter-active,
.more-sheet-leave-active {
  transition-property: opacity;
  transition-duration: 180ms;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);
}

.more-sheet-enter-active section,
.more-sheet-leave-active section {
  transition-property: transform, opacity;
  transition-duration: 180ms;
  transition-timing-function: cubic-bezier(0.2, 0, 0, 1);
}

.more-sheet-enter-from,
.more-sheet-leave-to,
.more-sheet-enter-from section,
.more-sheet-leave-to section {
  opacity: 0;
}

.more-sheet-enter-from section,
.more-sheet-leave-to section {
  transform: translateY(12px);
}
</style>
